import { useEffect, useMemo, useRef, useState } from "react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import {
  User as UserIcon,
  Mail,
  Building2,
  Phone,
  Bell,
  CreditCard,
  Lock,
  LogOut,
  Copy,
  Check,
  Save,
  Loader2,
  Shield,
  ExternalLink,
  Repeat,
  Sparkles,
} from "lucide-react";
import { toast } from "sonner";
import { format } from "date-fns";
import { SiteHeader } from "@/components/site-header";
import { SiteFooter } from "@/components/site-footer";
import { Breadcrumbs } from "@/components/breadcrumbs";
import { AuthGate } from "@/components/auth-gate";
import { useAuth } from "@/hooks/use-auth";
import { profileService } from "@/lib/services/profileService";
import { billingService } from "@/lib/services/billingService";
import { authService } from "@/lib/services/authService";
import { aliasForUserId } from "@/lib/email-intake/alias";
import { REVIEW_PRICE_LABEL, NEGOTIATION_PRICE_LABEL } from "@/lib/pricing";
import {
  DEFAULT_NOTIFICATION_PREFERENCES,
  type NotificationPreferences,
  type Profile,
  type BillingRecord,
} from "@/lib/db/models";

export const Route = createFileRoute("/settings")({
  head: () => ({
    meta: [
      { title: "ClaimGuard | Settings" },
      {
        name: "description",
        content: "Manage your profile, notification preferences, and billing.",
      },
    ],
  }),
  component: () => (
    <AuthGate>
      <SettingsPage />
    </AuthGate>
  ),
});

function SettingsPage() {
  const { user, roles } = useAuth();
  const isStaff = roles.includes("staff") || roles.includes("admin");

  return (
    <div className="min-h-dvh bg-background">
      <SiteHeader />
      <main className="container-page py-8">
        <Breadcrumbs items={[{ label: "Settings" }]} />
        <div className="mt-4">
          <h1 className="text-2xl font-semibold tracking-tight text-navy">Settings</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Manage your account, notifications, and billing.
          </p>
        </div>

        {user && (
          <div className="mt-8 grid gap-6 lg:grid-cols-3">
            <div className="space-y-6 lg:col-span-2">
              <ProfileCard userId={user.id} email={user.email ?? ""} />
              <PrivateEmailCard userId={user.id} />
              <NotificationsCard userId={user.id} />
              {!isStaff && <BillingCard userId={user.id} />}
              {!isStaff && <BillingPreferencesCard />}
              {isStaff && <StaffPanel roles={roles} />}
            </div>
            <div className="space-y-6">
              <SecurityCard />
            </div>
          </div>
        )}
      </main>
      <SiteFooter />
    </div>
  );
}

/* ---------- Shared card wrapper ---------- */

function Card({
  icon: Icon,
  title,
  subtitle,
  children,
}: {
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  subtitle?: string;
  children: React.ReactNode;
}) {
  return (
    <section className="rounded-2xl border border-border bg-card p-6 shadow-card md:p-7">
      <div className="flex items-start gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-trust-soft text-trust">
          <Icon className="h-5 w-5" />
        </div>
        <div className="flex-1">
          <h2 className="font-display text-lg text-navy">{title}</h2>
          {subtitle && <p className="mt-0.5 text-sm text-muted-foreground">{subtitle}</p>}
        </div>
      </div>
      <div className="mt-5">{children}</div>
    </section>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <div className="mb-1.5 text-xs font-medium uppercase tracking-wide text-muted-foreground">
        {label}
      </div>
      {children}
    </label>
  );
}

const inputCls =
  "w-full rounded-md border border-border bg-white px-3 py-2 text-sm text-navy shadow-sm outline-none transition focus:border-trust focus:ring-2 focus:ring-trust/20 disabled:cursor-not-allowed disabled:bg-muted/40";

/* ---------- Profile ---------- */

function ProfileCard({ userId, email }: { userId: string; email: string }) {
  const [profile, setProfile] = useState<Profile | null>(null);
  const [fullName, setFullName] = useState("");
  const [phone, setPhone] = useState("");
  const [company, setCompany] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const saveInFlight = useRef(false);

  useEffect(() => {
    profileService
      .getMyProfile(userId)
      .then((p) => {
        setProfile(p);
        setFullName(p?.full_name ?? "");
        setPhone(p?.phone ?? "");
        setCompany(p?.company ?? "");
      })
      .catch(() => toast.error("Couldn't load your profile."))
      .finally(() => setLoading(false));
  }, [userId]);

  async function save() {
    if (saveInFlight.current) return;
    saveInFlight.current = true;
    setSaving(true);
    try {
      const updated = await profileService.updateMyProfile(userId, {
        full_name: fullName.trim() || null,
        phone: phone.trim() || null,
        company: company.trim() || null,
      });
      setProfile(updated);
      toast.success("Profile updated");
    } catch (e) {
      toast.error((e as Error).message ?? "Couldn't update your profile");
    } finally {
      setSaving(false);
      saveInFlight.current = false;
    }
  }


  return (
    <Card icon={UserIcon} title="Profile" subtitle="Basic account and company information.">
      {loading ? (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading…
        </div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Full name">
            <input
              className={inputCls}
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              placeholder="Your name"
            />
          </Field>
          <Field label="Email">
            <input className={inputCls} value={email} disabled />
          </Field>
          <Field label="Phone">
            <div className="relative">
              <Phone className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <input
                className={`${inputCls} pl-9`}
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="+1 555 555 0100"
              />
            </div>
          </Field>
          <Field label="Company">
            <div className="relative">
              <Building2 className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <input
                className={`${inputCls} pl-9`}
                value={company}
                onChange={(e) => setCompany(e.target.value)}
                placeholder="Company name (optional)"
              />
            </div>
          </Field>
          <div className="sm:col-span-2 flex items-center justify-between border-t pt-4">
            <div className="text-xs text-muted-foreground">
              {profile?.updated_at && `Last updated ${format(new Date(profile.updated_at), "PPp")}`}
            </div>
            <button
              onClick={save}
              disabled={saving}
              className="inline-flex items-center gap-2 rounded-md bg-navy px-4 py-2 text-sm font-semibold text-navy-foreground transition hover:opacity-90 disabled:opacity-60"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
              Save changes
            </button>
          </div>
        </div>
      )}
    </Card>
  );
}

/* ---------- Private Submission Email ---------- */

function PrivateEmailCard({ userId }: { userId: string }) {
  const address = useMemo(() => aliasForUserId(userId), [userId]);
  const [copied, setCopied] = useState(false);
  return (
    <Card
      icon={Mail}
      title="Private Submission Email"
      subtitle="Forward invoices here anytime and we'll start your review automatically."
    >
      <div className="flex flex-wrap items-center gap-2 rounded-lg border border-border bg-secondary/40 p-3">
        <code className="flex-1 truncate font-mono text-sm text-navy">{address}</code>
        <button
          type="button"
          onClick={() => {
            navigator.clipboard.writeText(address);
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
          }}
          className="inline-flex items-center gap-1.5 rounded-md bg-navy px-3 py-1.5 text-xs font-semibold text-white transition hover:opacity-90"
        >
          {copied ? (
            <>
              <Check className="h-3.5 w-3.5" /> Copied
            </>
          ) : (
            <>
              <Copy className="h-3.5 w-3.5" /> Copy
            </>
          )}
        </button>
      </div>
      <p className="mt-3 text-xs text-muted-foreground">
        This alias is unique to your account. Attachments (PDF/JPG/PNG) are matched automatically.
      </p>
    </Card>
  );
}

/* ---------- Notifications ---------- */

const NOTIF_ROWS: { key: keyof NotificationPreferences; label: string; body: string }[] = [
  {
    key: "report_ready",
    label: "Report ready",
    body: "Your professional findings report is ready to view.",
  },
  {
    key: "review_updates",
    label: "Review updates",
    body: "Status changes on your invoice reviews.",
  },
  {
    key: "negotiation_updates",
    label: "Negotiation updates",
    body: "Progress on Full-Service Negotiation cases.",
  },
  { key: "billing_updates", label: "Billing updates", body: "Payments, refunds, and receipts." },
  { key: "marketing", label: "Product news", body: "Occasional product updates. Off by default." },
];

function NotificationsCard({ userId }: { userId: string }) {
  const [prefs, setPrefs] = useState<NotificationPreferences>(DEFAULT_NOTIFICATION_PREFERENCES);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    profileService
      .getMyProfile(userId)
      .then((p) => {
        setPrefs({ ...DEFAULT_NOTIFICATION_PREFERENCES, ...(p?.notification_preferences ?? {}) });
      })
      .catch(() => undefined)
      .finally(() => setLoading(false));
  }, [userId]);

  async function toggle(key: keyof NotificationPreferences) {
    const next = { ...prefs, [key]: !prefs[key] };
    setPrefs(next);
    setSaving(true);
    try {
      await profileService.updateMyProfile(userId, { notification_preferences: next });
    } catch (e) {
      setPrefs(prefs);
      toast.error((e as Error).message ?? "Couldn't update preferences");
    } finally {
      setSaving(false);
    }
  }

  return (
    <Card
      icon={Bell}
      title="Notification Preferences"
      subtitle={saving ? "Saving…" : "Choose what we notify you about."}
    >
      {loading ? (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading…
        </div>
      ) : (
        <ul className="divide-y">
          {NOTIF_ROWS.map((row) => (
            <li key={row.key} className="flex items-start justify-between gap-4 py-3.5">
              <div>
                <div className="text-sm font-semibold text-navy">{row.label}</div>
                <div className="text-xs text-muted-foreground">{row.body}</div>
              </div>
              <Toggle
                checked={!!prefs[row.key]}
                onChange={() => toggle(row.key)}
                label={row.label}
              />
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

function Toggle({
  checked,
  onChange,
  label,
}: {
  checked: boolean;
  onChange: () => void;
  label: string;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      onClick={onChange}
      className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition ${
        checked ? "bg-savings" : "bg-border"
      }`}
    >
      <span
        className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition ${
          checked ? "translate-x-5" : "translate-x-0.5"
        }`}
      />
    </button>
  );
}

/* ---------- Billing ---------- */

function BillingCard({ userId }: { userId: string }) {
  const [records, setRecords] = useState<BillingRecord[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    billingService
      .listMine(userId)
      .then(setRecords)
      .catch(() => undefined)
      .finally(() => setLoading(false));
  }, [userId]);

  const totalPaid = records
    .filter((r) => r.status === "paid" || r.status === "succeeded")
    .reduce((s, r) => s + Number(r.amount || 0), 0);

  return (
    <Card
      icon={CreditCard}
      title="Billing"
      subtitle="Payments for reviews and Full-Service Negotiations."
    >
      <div className="grid gap-3 sm:grid-cols-3">
        <Stat label="Total paid" value={money(totalPaid)} />
        <Stat label="Records" value={records.length.toString()} />
        <Stat label="Customer ID" value="Not linked yet" hint />
      </div>

      <div className="mt-5 overflow-hidden rounded-lg border">
        {loading ? (
          <div className="flex items-center gap-2 p-4 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading…
          </div>
        ) : records.length === 0 ? (
          <div className="p-6 text-center text-sm text-muted-foreground">
            No billing history yet.
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-muted/40 text-xs uppercase tracking-wide text-muted-foreground">
              <tr>
                <th className="px-3 py-2 text-left font-medium">Date</th>
                <th className="px-3 py-2 text-left font-medium">Type</th>
                <th className="px-3 py-2 text-left font-medium">Status</th>
                <th className="px-3 py-2 text-right font-medium">Amount</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {records.map((r) => (
                <tr key={r.id}>
                  <td className="px-3 py-2 text-muted-foreground">
                    {format(new Date(r.created_at), "PP")}
                  </td>
                  <td className="px-3 py-2 capitalize text-navy">{r.kind.replace(/_/g, " ")}</td>
                  <td className="px-3 py-2">
                    <StatusPill status={r.status} />
                  </td>
                  <td className="px-3 py-2 text-right font-semibold text-navy">
                    {money(Number(r.amount || 0), r.currency)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </Card>
  );
}

function Stat({ label, value, hint }: { label: string; value: string; hint?: boolean }) {
  return (
    <div className="rounded-lg border bg-secondary/30 p-3">
      <div className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className={`mt-1 font-semibold ${hint ? "text-muted-foreground" : "text-navy"}`}>
        {value}
      </div>
    </div>
  );
}

function StatusPill({ status }: { status: string }) {
  const good = status === "paid" || status === "succeeded";
  const bad = status === "failed" || status === "canceled";
  return (
    <span
      className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium capitalize ${
        good
          ? "bg-savings/15 text-savings"
          : bad
            ? "bg-destructive/10 text-destructive"
            : "bg-muted text-muted-foreground"
      }`}
    >
      {status}
    </span>
  );
}

function money(n: number, currency = "USD") {
  try {
    return new Intl.NumberFormat("en-US", { style: "currency", currency }).format(n);
  } catch {
    return `$${n.toFixed(2)}`;
  }
}

/* ---------- Billing Preferences (Coming Soon) ---------- */

function BillingPreferencesCard() {
  return (
    <Card
      icon={Repeat}
      title="Billing Preferences"
      subtitle="Streamline repeat submissions with saved payment and default service selection."
    >
      <div className="flex flex-wrap items-center gap-2 rounded-lg border border-dashed border-trust/30 bg-trust-soft/40 px-3 py-2 text-xs font-medium text-trust">
        <Sparkles className="h-3.5 w-3.5" />
        Coming soon — no changes to how you're billed today.
      </div>

      <fieldset
        disabled
        aria-disabled="true"
        className="mt-5 space-y-5 opacity-70"
      >
        {/* Saved payment method */}
        <div className="rounded-xl border border-border bg-secondary/40 p-4">
          <div className="flex items-start justify-between gap-3">
            <div>
              <div className="text-sm font-semibold text-navy">
                Saved payment method
              </div>
              <p className="mt-1 text-xs text-muted-foreground">
                Keep a card on file so future invoice submissions charge automatically.
              </p>
            </div>
            <ComingSoonPill />
          </div>
          <div className="mt-3 flex items-center gap-3 rounded-lg border border-dashed border-border bg-white/70 px-3 py-3">
            <div className="flex h-9 w-12 items-center justify-center rounded-md border border-border bg-white text-muted-foreground">
              <CreditCard className="h-4 w-4" />
            </div>
            <div className="flex-1 text-sm">
              <div className="font-medium text-navy/70">No payment method saved</div>
              <div className="text-xs text-muted-foreground">
                You'll be able to add and manage cards here.
              </div>
            </div>
            <button
              type="button"
              disabled
              className="cursor-not-allowed rounded-md border border-border bg-white px-3 py-1.5 text-xs font-semibold text-navy/60"
            >
              Add card
            </button>
          </div>
        </div>

        {/* Automatic charging */}
        <PreferenceToggle
          title="Automatically charge saved payment method"
          description="When you upload a new invoice, we'll charge your saved card and start the review right away."
        />

        {/* Default service selection */}
        <div className="rounded-xl border border-border bg-secondary/40 p-4">
          <div className="flex items-start justify-between gap-3">
            <div>
              <div className="text-sm font-semibold text-navy">
                Default service selection
              </div>
              <p className="mt-1 text-xs text-muted-foreground">
                Preselect the service that fits your workflow. You can still change it on any upload.
              </p>
            </div>
            <ComingSoonPill />
          </div>
          <div className="mt-3 grid gap-2 sm:grid-cols-2">
            <DefaultServiceOption
              label="Professional AI Review"
              price={REVIEW_PRICE_LABEL}
              tagline="Findings report you can act on."
            />
            <DefaultServiceOption
              label="Full-Service Negotiation"
              price={NEGOTIATION_PRICE_LABEL}
              tagline="Our specialists negotiate for you. Pricing is personalized after a specialist reviews your AI Findings Report."
            />
          </div>
        </div>

        {/* Future flow preview */}
        <div className="rounded-xl border border-dashed border-border bg-white/60 p-4">
          <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            Future repeat-customer flow
          </div>
          <ol className="mt-3 grid gap-2 text-xs text-navy/80 sm:grid-cols-4">
            <FlowStep n={1} label="Upload" />
            <FlowStep n={2} label="Automatic payment" />
            <FlowStep n={3} label="Review begins" />
            <FlowStep n={4} label="Dashboard" />
          </ol>
        </div>
      </fieldset>
    </Card>
  );
}

function ComingSoonPill() {
  return (
    <span className="shrink-0 rounded-full border border-trust/30 bg-white px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-trust">
      Coming soon
    </span>
  );
}

function PreferenceToggle({
  title,
  description,
}: {
  title: string;
  description: string;
}) {
  return (
    <div className="flex items-start justify-between gap-4 rounded-xl border border-border bg-secondary/40 p-4">
      <div>
        <div className="text-sm font-semibold text-navy">{title}</div>
        <p className="mt-1 text-xs text-muted-foreground">{description}</p>
      </div>
      <div className="flex items-center gap-2">
        <ComingSoonPill />
        <span
          aria-hidden
          className="relative inline-flex h-6 w-11 shrink-0 items-center rounded-full bg-muted"
        >
          <span className="h-5 w-5 translate-x-0.5 rounded-full bg-white shadow" />
        </span>
      </div>
    </div>
  );
}

function DefaultServiceOption({
  label,
  price,
  tagline,
}: {
  label: string;
  price: string;
  tagline: string;
}) {
  return (
    <div className="rounded-lg border border-dashed border-border bg-white/70 p-3">
      <div className="flex items-center justify-between gap-2">
        <div className="text-sm font-semibold text-navy/80">{label}</div>
        <span className="shrink-0 text-right text-xs font-semibold text-trust">{price}</span>
      </div>
      <div className="mt-1 text-xs text-muted-foreground">{tagline}</div>
      <div className="mt-2 inline-flex items-center gap-1.5 text-[11px] font-medium text-muted-foreground">
        <span className="h-3 w-3 rounded-full border border-border bg-white" />
        Set as default
      </div>
    </div>
  );
}

function FlowStep({ n, label }: { n: number; label: string }) {
  return (
    <li className="flex items-center gap-2 rounded-lg border border-border bg-white/70 px-3 py-2">
      <span className="flex h-5 w-5 items-center justify-center rounded-full bg-trust-soft text-[10px] font-bold text-trust">
        {n}
      </span>
      <span className="font-medium">{label}</span>
    </li>
  );
}


/* ---------- Security ---------- */

function SecurityCard() {
  const navigate = useNavigate();
  async function signOut() {
    await authService.signOut();
    navigate({ to: "/" });
  }
  return (
    <Card icon={Lock} title="Security" subtitle="Password and session.">
      <div className="space-y-3">
        <Link
          to="/reset-password"
          className="flex items-center justify-between rounded-lg border p-3 text-sm hover:bg-accent"
        >
          <span className="font-medium text-navy">Change password</span>
          <ExternalLink className="h-4 w-4 text-muted-foreground" />
        </Link>
        <button
          onClick={signOut}
          className="flex w-full items-center justify-center gap-2 rounded-lg border border-destructive/30 bg-destructive/5 px-3 py-2.5 text-sm font-semibold text-destructive transition hover:bg-destructive/10"
        >
          <LogOut className="h-4 w-4" /> Sign out
        </button>
      </div>
    </Card>
  );
}

/* ---------- Staff panel ---------- */

function StaffPanel({ roles }: { roles: string[] }) {
  return (
    <Card icon={Shield} title="Staff" subtitle="Your role and operations access.">
      <div className="grid gap-3 sm:grid-cols-2">
        <Stat label="Role" value={roles.join(", ") || "staff"} />
        <Link
          to="/operations"
          className="flex items-center justify-between rounded-lg border bg-secondary/30 p-3 text-sm hover:bg-accent"
        >
          <span className="font-semibold text-navy">Open Operations Center</span>
          <ExternalLink className="h-4 w-4 text-muted-foreground" />
        </Link>
      </div>
    </Card>
  );
}
